import { motion } from "framer-motion";
import { HERO_IMG } from "../../lib/api";
import { ArrowDownRight } from "lucide-react";

const LINES = ["MOTION,", "MACHINED TO", "THE ARCSECOND."];

const container = { hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } } };
const lineUp = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section className="relative min-h-screen flex items-end overflow-hidden tech-grid" data-testid="hero">
      {/* bg image */}
      <motion.div
        initial={{ scale: 1.12, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-0"
      >
        <img src={HERO_IMG} alt="Precision harmonic drive" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-transparent to-transparent" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pb-20 pt-32">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
          className="flex items-center gap-3 mb-6">
          <span className="w-10 h-px bg-signal" />
          <span className="font-mono text-[11px] tracking-[0.35em] uppercase text-signal">
            Precision Motion Components · Vadodara, India
          </span>
        </motion.div>

        <motion.h1 variants={container} initial="hidden" animate="show"
          className="font-head font-black uppercase text-white leading-[0.9] text-5xl sm:text-7xl lg:text-8xl">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span variants={lineUp} className="block">
                {i === 2 ? <span className="text-signal">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <div className="grid lg:grid-cols-12 gap-8 mt-10 items-end">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="lg:col-span-6 text-slate-300 text-base sm:text-lg leading-relaxed max-w-xl">
            Sourcing partner for harmonic drive reducers, robotic joint modules, QDD & frameless
            torque motors and electric grippers — direct from <span className="text-white font-semibold">the manufacturers</span>.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}
            className="lg:col-span-6 flex flex-wrap gap-4 lg:justify-end">
            <button onClick={() => scrollTo("catalog")} data-testid="hero-catalog-btn"
              className="group font-head font-bold uppercase tracking-wider px-7 py-4 bg-signal text-ink hover:bg-signal-hover transition-colors flex items-center gap-2">
              Explore Catalog <ArrowDownRight className="w-5 h-5 group-hover:translate-x-1 group-hover:translate-y-1 transition-transform" />
            </button>
            <button onClick={() => scrollTo("enquiry")} data-testid="hero-enquiry-btn"
              className="font-head font-bold uppercase tracking-wider px-7 py-4 border border-white/20 text-white hover:border-signal hover:text-signal transition-colors">
              Request a Quote
            </button>
          </motion.div>
        </div>

        {/* stat strip */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}
          className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {[["< 1", "arcmin backlash"], ["12+", "product lines"], ["4", "global tier-1 partners"], ["350 Nm", "peak torque range"]].map(([n, l]) => (
            <div key={l} className="bg-ink/80 p-5">
              <div className="font-head font-extrabold text-3xl text-white">{n}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mt-1">{l}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
